import { HStack, Spacer, Stack, Text } from "@chakra-ui/react";
import * as React from "react";
import { LuPencil, LuPlus, LuStar } from "react-icons/lu";
import { useParams } from "react-router-dom";

import {
  deleteSocialProof,
  getBusinessFeatures,
  getCustomerProfileSubsets,
  getMessagingPillars,
  getSocialProofs,
} from "../../../api/company-knowledge";
import { Button } from "../../../components/button";
import DataTable, { DataTableColumn } from "../../../components/data-table";
import { DeleteButton } from "../../../components/delete-button";
import { EmptyState } from "../../../components/empty-state";
import IconCircle from "../../../components/icon-circle";
import PageBody from "../../../components/page-body";
import { SocialProofDialog } from "../../../components/social-proof-dialog";
import { useLoading, useToaster } from "../../../hooks";
import {
  BusinessFeature,
  CustomerProfileSubset,
  MessagingPillar,
  SocialProof,
} from "../../../types/company-knowledge";

export default function SocialProofsPage() {
  const { workspace: workspaceSlug } = useParams();
  const [proofs, setProofs] = React.useState<SocialProof[]>([]);
  const [subsets, setSubsets] = React.useState<CustomerProfileSubset[]>([]);
  const [features, setFeatures] = React.useState<BusinessFeature[]>([]);
  const [pillars, setPillars] = React.useState<MessagingPillar[]>([]);
  const [isLoading, withLoading] = useLoading();
  const [hasLoadedOnce, setHasLoadedOnce] = React.useState(false);
  const [isDialogOpen, setIsDialogOpen] = React.useState(false);
  const [editingProof, setEditingProof] = React.useState<SocialProof | null>(
    null,
  );
  const withToaster = useToaster();

  const loadProofs = React.useCallback(async () => {
    if (!workspaceSlug) return;
    await withLoading(async () => {
      await withToaster(async () => {
        // The subsets, features, and pillars ride along on every load so
        // the dialog's checkbox groups stay in sync.
        const [proofs, subsets, features, pillars] = await Promise.all([
          getSocialProofs(workspaceSlug),
          getCustomerProfileSubsets(workspaceSlug),
          getBusinessFeatures(workspaceSlug),
          getMessagingPillars(workspaceSlug),
        ]);
        setProofs(proofs);
        setSubsets(subsets);
        setFeatures(features);
        setPillars(pillars);
        setHasLoadedOnce(true);
      });
    });
  }, [withLoading, withToaster, workspaceSlug]);

  React.useEffect(() => {
    loadProofs();
  }, [loadProofs]);

  const handleDelete = React.useCallback(
    async (id: number) => {
      if (!workspaceSlug) return;
      await withLoading(async () => {
        await withToaster(async () => {
          await deleteSocialProof(workspaceSlug, id);
          await loadProofs();
        });
      });
    },
    [loadProofs, withLoading, withToaster, workspaceSlug],
  );

  const columns: DataTableColumn<SocialProof>[] = [
    {
      id: "title",
      name: "Title",
      icon: <LuStar />,
      width: 240,
      cell: ({ value }) => <Text fontWeight="medium">{value.title}</Text>,
    },
    {
      id: "customer",
      name: "Customer",
      width: 200,
      cell: ({ value }) => (
        <Text color="fg.muted" truncate>
          {value.customer || "—"}
        </Text>
      ),
    },
    {
      name: "Narrative",
      width: 320,
      cell: ({ value }) => (
        <Text color="fg.muted" truncate>
          {value.narrative || "—"}
        </Text>
      ),
    },
    {
      name: "Applicable ICP subsets",
      width: 200,
      cell: ({ value }) => {
        const names = subsets
          .filter((s) => value["subset-ids"].includes(s.id))
          .map((s) => s.name);
        return (
          <Text color="fg.muted" truncate>
            {names.length > 0 ? names.join(", ") : "All subsets"}
          </Text>
        );
      },
    },
    {
      name: "",
      width: 100,
      cell: ({ value }) => (
        <HStack justify="flex-end">
          <Button
            size="sm"
            title="Edit social proof"
            variant="ghost"
            onClick={() => {
              setEditingProof(value);
              setIsDialogOpen(true);
            }}
          >
            <LuPencil />
          </Button>
          <DeleteButton
            label="Delete"
            message={`Delete "${value.title}"?`}
            onDelete={() => handleDelete(value.id)}
          />
        </HStack>
      ),
    },
  ];

  const isEmpty = proofs.length === 0 && hasLoadedOnce;

  return (
    <PageBody>
      <Stack flex="1" gap="5">
        <HStack>
          <Stack gap="0.5">
            <Text color="fg.ink" fontSize="md" fontWeight="medium">
              Social Proof
            </Text>
            <Text color="fg.muted" fontSize="sm">
              Case studies and testimonials the product can cite, with the
              evidence to back them.
            </Text>
          </Stack>
          {!isEmpty && (
            <>
              <Spacer />
              <Button
                onClick={() => {
                  setEditingProof(null);
                  setIsDialogOpen(true);
                }}
              >
                <LuPlus /> Create Social Proof
              </Button>
            </>
          )}
        </HStack>
        {isEmpty ? (
          <HStack flex="1" alignItems="center">
            <EmptyState
              icon={<IconCircle Icon={LuStar} size="md" variant="flat" />}
              title="No Social Proofs"
              description="Add case studies and testimonials worth citing."
            >
              <Button
                onClick={() => {
                  setEditingProof(null);
                  setIsDialogOpen(true);
                }}
              >
                <LuPlus /> Create Social Proof
              </Button>
            </EmptyState>
          </HStack>
        ) : (
          <DataTable
            data={proofs}
            columns={columns}
            skeleton={isLoading && hasLoadedOnce ? 3 : undefined}
          />
        )}
      </Stack>
      <SocialProofDialog
        features={features}
        isOpen={isDialogOpen}
        onOpenChange={({ open }) => {
          if (!open) setEditingProof(null);
          setIsDialogOpen(open);
        }}
        onSuccess={loadProofs}
        pillars={pillars}
        proof={editingProof}
        subsets={subsets}
        workspaceSlug={workspaceSlug ?? ""}
      />
    </PageBody>
  );
}
